import { IGetOptimizelyFlagsAction, IGetOptimizelyFlagVariationAction } from 'app/models/Optimizely/Optimizely';
import axios, { AxiosError, AxiosRequestConfig, CancelToken } from 'axios';
import { baseAxiosInstance } from 'utils/BaseApi';
import { cacheResponse, CacheType, getCachedResponse } from 'utils/networkCache';
import { httpSuccess } from '../serviceHelpers';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { ICreateUserPayload } from '@AMIEWEB/Settings/UserManagement/store/userManagement.redux';
import { IUpdateOrderTargetMargin } from '@AMIEWEB/Order/Store/Order.redux';
import { IGetResetOrderPayPackagesPayload } from '@AMIEWEB/Order/Store/types';

export class SharedService {
  private sharedBase: string;

  private cancellationToken = axios.CancelToken.source();

  private static currentInstance: SharedService;

  config?: AxiosRequestConfig = {};

  private constructor() {
    this.sharedBase = globalThis?.app?.env?.REACT_APP_TRACK_CHANGE_SERVICE_BASE_URL;
  }

  static createInstance(): SharedService {
    const activeInstance = new SharedService();
    // activeInstance.cancellationToken = axios.CancelToken.source();
    // if (activeInstance.config) activeInstance.config.cancelToken = activeInstance.cancellationToken.token;
    return activeInstance;
  }

  static getInstance(): SharedService {
    if (!this.currentInstance) this.currentInstance = this.createInstance();
    return this.currentInstance;
  }

  static cancelActiveRequests() {
    this.currentInstance.cancellationToken.cancel('RequestCancellation');
    // this.currentInstance = null;
  }

  cancelRequests() {
    this.cancellationToken.cancel('RequestCancellation');
    return SharedService.getInstance();
  }

  cancelAndRevokeInstance() {
    this.cancellationToken.cancel('RequestCancellation');
    this.cancellationToken = axios.CancelToken.source();
    if (this.config) this.config.cancelToken = this.cancellationToken.token;
  }

  getOptimizelyFlags = ({ perPage, archived }: IGetOptimizelyFlagsAction) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/optimizely/flags?perPage=${perPage}&archived=${archived}`)
      .then(response => response)
      .catch(error => error.response);

  getOptimizelyFlagVariant = ({ flagKey, perPage, archived }: IGetOptimizelyFlagVariationAction) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/optimizely/flags/${flagKey}/variations?perPage=${perPage}&archived=${archived}`)
      .then(response => response)
      .catch(error => error.response);

  getEmployeeExternalSystemDetails = (employeeId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/users/external-systems/${employeeId}`)
      .then(response => response)
      .catch(err => err.response);

  getPlacementDocument = (docId: string) =>
    baseAxiosInstance.post(`${this.sharedBase}/placement/document/download`, { docId }).catch(err => err.response);

  getWageRequestDocument = (docId: string, candidateId: string) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/document/wageRequestForm/${candidateId}/${docId}`)
      .catch(err => err.response);

  getPlacementClassicDocument = (documentType: string, documentId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/document/documentType/${documentType}/${documentId}`)
      .catch(err => err.response);

  getSkillsChecklistDocument = (documentId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/document/documentType/skillschecklist/${documentId}`, {
        params: { convertToPDF: true },
      })
      .catch(err => err.response);

  getDisciplineSpecialtyData = () =>
    baseAxiosInstance
      .get(`${this.sharedBase}/ConfigInfo/disciplinesSpecialtiesSubSpecialtiesList`)
      .catch(err => err.response);

  getAppVersion = () =>
    axios.get(`${globalThis?.app?.env?.REACT_APP_BASE_URL}/appVersion.json`).catch(err => err.response);

  getExceptionsPayRateGww = (excludeOther: boolean) =>
    baseAxiosInstance.get(`${this.sharedBase}/pick-list/gww-exceptions?excludeOther=${excludeOther}`);

  getScreenOptions = async (cacheControl: boolean = true) => {
    try {
      const url = `${this.sharedBase}/search/lookups/screen`;
      if (cacheControl) {
        const cachedData = await getCachedResponse(CacheType.searchOptions, url);
        if (!isNullOrUndefined(cachedData)) {
          return cachedData;
        }
      }
      const response = await baseAxiosInstance.get(url);
      if (cacheControl && httpSuccess(response.status)) {
        cacheResponse(CacheType.searchOptions, url, response);
      }
      return response;
    } catch (error) {
      return err => err.response;
    }
  };

  getUserLists = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/search`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getUserDetails = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/employee-details-query`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getEmployeeRoles = async employeeId => {
    try {
      const response = await baseAxiosInstance.get(`${this.sharedBase}/users/${employeeId}/roles`);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getEmployeeAssociations = async employeeId => {
    try {
      const response = await baseAxiosInstance.get(`${this.sharedBase}/users/${employeeId}/associations`);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getEmployeeAssociationTypeOptions = async () => {
    try {
      const response = await baseAxiosInstance.get(`${this.sharedBase}/search/lookups/employeeassociationtype`);
      return response.data;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  updateUserInformation = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/employee-details-update`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  updateUserRoles = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/roles/update`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  createNewUser = async (data: ICreateUserPayload) => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/create-user`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  updateAssociations = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/associations/update`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getEmployeeInfo = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/users/info/slim`, data);
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getFacilityDrugScreenPolicyOptions = async () => {
    try {
      const response = await baseAxiosInstance.get(`${this.sharedBase}/search/lookups/drugPolicyOptions`);
      return response.data;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getUserPreferences = async () => {
    try {
      const response = await baseAxiosInstance.get(`${this.sharedBase}/users/preferences`);
      if (httpSuccess(response.status))
        response.data = response.data.map(({ value, ...rest }) => ({ ...rest, value: JSON.parse(value) }));
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  saveUserPreferences = async (data, cancelToken: CancelToken) => {
    try {
      const response = await baseAxiosInstance.post(
        `${this.sharedBase}/users/preferences`,
        data.map(({ value, ...rest }) => ({ ...rest, value: JSON.stringify(value) })),
        { cancelToken },
      );
      if (httpSuccess(response.status))
        response.data = response.data.map(({ value, ...rest }) => ({ ...rest, value: JSON.parse(value) }));
      return response;
    } catch (error) {
      return (err: { response: any }) => err.response;
    }
  };

  getR225Report = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/recruiter/sales-report`, data);
      return response;
    } catch (error) {
      return error;
    }
  };

  getR448Report = async data => {
    try {
      const response = await baseAxiosInstance.post(`${this.sharedBase}/booking/details-report`, data);
      return response;
    } catch (error) {
      return error;
    }
  };

  getPlacementMarginOrderDetails = (placementId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/placement/${placementId}/margin-order-details`)
      .catch((error: AxiosError) => error.response);

  getChangeHistory = payload => baseAxiosInstance.post(`${this.sharedBase}/change-tracker`, payload);

  getOrderTargetMargins = (orderId: number, userId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/orders/margin/target/${orderId}/${userId}`)
      .catch((error: AxiosError) => error.response);

  updateOrderTargetMargins = (data: IUpdateOrderTargetMargin) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/target/update`, data)
      .catch((error: AxiosError) => error.response);

  getOrderPayPackages = (query: { orderId?: number; facilityId?: number }) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/orders/margin/margin-inline-details`, {
        params: {
          ...(query?.orderId ? { orderId: query.orderId } : {}),
          ...(query?.facilityId ? { facilityId: query.facilityId } : {}),
        },
      })
      .catch((error: AxiosError) => error.response);

  getPlacementPayPackages = (payload: import('app/components/Order/Store/types').IPlacementPayPackagesRequest) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/placement/margin/margin-inline-details/placements`, payload)
      .catch((error: AxiosError) => error.response);

  getPlacementPayPackagesFilterValues = (query: { facilityId?: number; orderId?: number; candidateId?: number }) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/placement/margin/margin-inline-details/placements/filter-values`, {
        params: {
          ...(query?.facilityId != null ? { facilityId: query.facilityId } : {}),
          ...(query?.orderId != null ? { orderId: query.orderId } : {}),
          ...(query?.candidateId != null ? { candidateId: query.candidateId } : {}),
        },
      })
      .catch((error: AxiosError) => error.response);

  getPeopleSoftCalculatedValues = (data: any) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/scenario-details/inline-update`, data)
      .catch((error: AxiosError) => error.response);
  saveOrderPaypackages = (data: any) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/scenario-details/inline-bulk-update`, data)
      .catch((error: AxiosError) => error.response);
  getOrderPackagesResetValues = (data: IGetResetOrderPayPackagesPayload) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/scenario-details/inline-refresh`, data)
      .catch((error: AxiosError) => error.response);
  getTargetMarginResetValues = (data: IGetResetOrderPayPackagesPayload) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/target/refresh`, data)
      .catch((error: AxiosError) => error.response);
  getCheckActiveMarginJobsStatus = (orderId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/orders/margin/${orderId}/check-active-margin-jobs-status`)
      .then(response => response)
      .catch((error: AxiosError) => error.response);

  getScenarioHistoryDetails = (scenarioId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/orders/margin/${scenarioId}/scenario-history-details`)
      .then(response => response.data)
      .catch((error: AxiosError) => {
        console.error('Error fetching scenario history details:', error);
        throw error;
      });

  getScenarioHistorySimilarDetails = (scenarioId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/orders/margin/${scenarioId}/scenario-history-similar-details`)
      .then(response => response.data)
      .catch((error: AxiosError) => {
        console.error('Error fetching scenario history similar details:', error);
        throw error;
      });
  getFacilityTargetMargins = (facilityId: number) =>
    baseAxiosInstance
      .get(`${this.sharedBase}/orders/margin/facility/target/${facilityId}`)
      .then(response => response)
      .catch((error: AxiosError) => {
        console.error('Error fetching facility target margins:', error);
        throw error;
      });
  saveFacilityTargetMargins = (data: any) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/facility/target//bulk_upsert`, data)
      .then(response => response)
      .catch((error: AxiosError) => {
        console.error('Error saving facility target margins:', error);
        throw error;
      });
  resetFacilityTargetMargins = (data: any) =>
    baseAxiosInstance
      .post(`${this.sharedBase}/orders/margin/facility/target/refresh`, data)
      .then(response => response)
      .catch((error: AxiosError) => {
        console.error('Error resetting facility target margins:', error);
        throw error;
      });
}
